import React, { useCallback, useEffect, useState } from 'react';
import leaseService from '../../services/leaseService';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import FormField from '../common/FormField';
import { formatDate } from '../../utils/formatters';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const emptyForm = { start_date: '', end_date: '', monthly_rent: '', deposit_amount: '', notice_period_days: '' };

const LeaseForm = ({ initial, submitLabel, onSubmit, busy }) => {
  const [values, setValues] = useState(initial || emptyForm);
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(values); }}>
      <FormField label="Start date" name="start_date" type="date" value={values.start_date} onChange={set('start_date')} required />
      <FormField label="End date" name="end_date" type="date" value={values.end_date} onChange={set('end_date')} required />
      <FormField label="Monthly rent (R)" name="monthly_rent" type="number" value={values.monthly_rent} onChange={set('monthly_rent')} required />
      <FormField label="Deposit (R)" name="deposit_amount" type="number" value={values.deposit_amount} onChange={set('deposit_amount')} />
      <FormField label="Notice period (days)" name="notice_period_days" type="number" value={values.notice_period_days} onChange={set('notice_period_days')} placeholder="30" />
      <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Saving…' : submitLabel}</button>
    </form>
  );
};

const LeaseCard = ({ lease, onReload, setNotice }) => {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const download = async () => {
    try {
      const blob = await leaseService.downloadDocument(lease.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = lease.document_original_filename || 'lease.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This document could not be opened.') });
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await leaseService.uploadDocument(lease.id, file);
      setNotice({ type: 'success', message: 'Lease document uploaded.' });
      onReload();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This document could not be uploaded.') });
    } finally {
      setUploading(false);
    }
  };

  const giveNotice = async () => {
    setBusy(true);
    try {
      await leaseService.giveNotice(lease.id);
      setNotice({ type: 'success', message: 'Notice recorded on this lease.' });
      onReload();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'Notice could not be recorded.') });
    } finally {
      setBusy(false);
    }
  };

  const terminate = async () => {
    setBusy(true);
    try {
      await leaseService.terminate(lease.id);
      setNotice({ type: 'success', message: 'Lease terminated.' });
      onReload();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This lease could not be terminated.') });
    } finally {
      setBusy(false);
    }
  };

  const renew = async (values) => {
    setBusy(true);
    try {
      await leaseService.renew(lease.id, values);
      setNotice({ type: 'success', message: 'Lease renewed.' });
      setRenewing(false);
      onReload();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This lease could not be renewed.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <strong>{formatDate(lease.start_date)} — {formatDate(lease.end_date)}</strong>
          <StatusBadge status={lease.status} />
        </div>
        <p className="ap-muted" style={{ marginTop: 4 }}>
          R {lease.monthly_rent.toFixed(2)}/month
          {lease.deposit_amount != null && ` · Deposit R ${lease.deposit_amount.toFixed(2)}`}
          {' · '}{lease.notice_period_days}-day notice period
        </p>

        {lease.has_document ? (
          <p><button type="button" className="btn-action" onClick={download}>Download {lease.document_original_filename}</button></p>
        ) : (
          <div className="form-group">
            <label className="form-label">Upload signed lease document</label>
            <input type="file" onChange={upload} disabled={uploading} />
          </div>
        )}

        {lease.notice_given_at && (
          <p className="ap-muted">Notice given by {lease.notice_given_by} on {formatDate(lease.notice_given_at)}.</p>
        )}

        {lease.status === 'active' && !renewing && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn-secondary" onClick={() => setRenewing(true)} disabled={busy}>Renew</button>
            <button type="button" className="btn-secondary" onClick={giveNotice} disabled={busy}>Give notice</button>
            <button type="button" className="btn-secondary" onClick={terminate} disabled={busy}>Terminate</button>
          </div>
        )}
        {lease.status === 'notice_given' && (
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn-secondary" onClick={terminate} disabled={busy}>Terminate now</button>
          </div>
        )}

        {renewing && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <h4>Renew this lease</h4>
            <LeaseForm
              initial={{ ...emptyForm, monthly_rent: lease.monthly_rent, deposit_amount: lease.deposit_amount ?? '', notice_period_days: lease.notice_period_days }}
              submitLabel="Renew"
              onSubmit={renew}
              busy={busy}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const LeaseSection = ({ applicationId }) => {
  const [leases, setLeases] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    leaseService.forApplication(applicationId).then(setLeases).catch((err) => setError(messageFrom(err, 'Leases could not be loaded.')));
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const create = async (values) => {
    setCreating(true);
    setNotice(null);
    try {
      await leaseService.create(applicationId, values);
      setNotice({ type: 'success', message: 'Lease created.' });
      load();
    } catch (err) {
      setNotice({ type: 'error', message: messageFrom(err, 'This lease could not be created. The application must have an approved decision first.') });
    } finally {
      setCreating(false);
    }
  };

  if (error) return <Alert type="error" message={error} />;
  if (!leases) return <LoadingSpinner />;

  const current = leases.find((l) => l.status === 'active' || l.status === 'notice_given');

  return (
    <div className="detail-section">
      <h3>Lease</h3>
      {notice && <Alert type={notice.type} message={notice.message} dismissible onClose={() => setNotice(null)} />}

      {leases.length === 0 ? (
        <>
          <p className="ap-muted">No lease has been created yet. This application must have an approved decision first.</p>
          <LeaseForm submitLabel="Create lease" onSubmit={create} busy={creating} />
        </>
      ) : (
        <>
          {current && <LeaseCard lease={current} onReload={load} setNotice={setNotice} />}
          {leases.filter((l) => l !== current).length > 0 && (
            <>
              <h4>History</h4>
              {leases.filter((l) => l !== current).map((l) => (
                <LeaseCard key={l.id} lease={l} onReload={load} setNotice={setNotice} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LeaseSection;
